/**
 * Date keys are plain 'YYYY-MM-DD' strings (no time zone). Month keys are 'YYYY-MM'.
 * All arithmetic runs at 12:00 UTC so that no DST shift can move a day.
 */
export type DateKey = string;
export type MonthKey = string;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;

export const isDateKey = (s: unknown): s is DateKey =>
  typeof s === 'string' && DATE_RE.test(s) && !Number.isNaN(Date.parse(s + 'T12:00:00Z')) && new Date(s + 'T12:00:00Z').toISOString().slice(0, 10) === s;
export const isMonthKey = (s: unknown): s is MonthKey => typeof s === 'string' && MONTH_RE.test(s) && +s.slice(5, 7) >= 1 && +s.slice(5, 7) <= 12;

export const toDate = (k: DateKey): Date => new Date(k + 'T12:00:00Z');
export const fromDate = (d: Date): DateKey => d.toISOString().slice(0, 10);

export function addDays(k: DateKey, n: number): DateKey {
  const d = toDate(k);
  d.setUTCDate(d.getUTCDate() + n);
  return fromDate(d);
}

/** 0 = Sunday … 6 = Saturday */
export const dayOfWeek = (k: DateKey): number => toDate(k).getUTCDay();
export const isWeekend = (k: DateKey): boolean => {
  const d = dayOfWeek(k);
  return d === 0 || d === 6;
};

/** The Monday of the ISO week that contains `k`. */
export const mondayOf = (k: DateKey): DateKey => addDays(k, -((dayOfWeek(k) + 6) % 7));

/** ISO week number. */
export function isoWeek(k: DateKey): number {
  const d = toDate(k);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3); // Thursday of this week
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4, 12));
  const ftDay = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ftDay + 3);
  return 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 864e5));
}

export const monthOf = (k: DateKey): MonthKey => k.slice(0, 7);

export function nextMonth(m: MonthKey): MonthKey {
  let y = +m.slice(0, 4);
  let mo = +m.slice(5, 7) + 1;
  if (mo > 12) {
    mo = 1;
    y++;
  }
  return `${y}-${String(mo).padStart(2, '0')}`;
}

export function prevMonth(m: MonthKey): MonthKey {
  let y = +m.slice(0, 4);
  let mo = +m.slice(5, 7) - 1;
  if (mo < 1) {
    mo = 12;
    y--;
  }
  return `${y}-${String(mo).padStart(2, '0')}`;
}

export const firstOfMonth = (m: MonthKey): DateKey => m + '-01';
export const lastOfMonth = (m: MonthKey): DateKey => addDays(firstOfMonth(nextMonth(m)), -1);

/** A week belongs to the month its Thursday is in (owner answer 3, 2026-10-05). */
export const monthOfWeek = (weekMonday: DateKey): MonthKey => monthOf(addDays(mondayOf(weekMonday), 3));

/** The weeks of a month: Mondays of the ISO weeks whose Thursday lies in the month. */
export function weeksOfMonth(m: MonthKey): DateKey[] {
  const out: DateKey[] = [];
  let k = mondayOf(firstOfMonth(m));
  for (let i = 0; i < 7; i++) {
    if (monthOfWeek(k) === m) out.push(k);
    k = addDays(k, 7);
  }
  return out;
}

/** Inclusive list of days. */
export function daysBetween(from: DateKey, to: DateKey): DateKey[] {
  const out: DateKey[] = [];
  for (let k = from; k <= to; k = addDays(k, 1)) out.push(k);
  return out;
}

const MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MON_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON_SV = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_SV = ['sön', 'mån', 'tis', 'ons', 'tors', 'fre', 'lör'];

/** "27 Oct" */
export const fmtDayMonth = (k: DateKey): string => `${+k.slice(8, 10)} ${MON_SHORT[+k.slice(5, 7) - 1]}`;
/** "Tue 27 Oct" */
export const fmtDay = (k: DateKey): string => `${DOW_SHORT[dayOfWeek(k)]} ${fmtDayMonth(k)}`;
/** "November 2026" */
export const fmtMonth = (m: MonthKey): string => `${MON_LONG[+m.slice(5, 7) - 1]} ${m.slice(0, 4)}`;
export const monthName = (m: MonthKey): string => MON_LONG[+m.slice(5, 7) - 1] ?? m;
/** Swedish month name, lower case ("november") */
export const monthNameSv = (m: MonthKey): string => MON_SV[+m.slice(5, 7) - 1] ?? m;
/** "lör 31 okt" style Swedish day label */
export const fmtDaySv = (k: DateKey): string => `${DOW_SV[dayOfWeek(k)]} ${+k.slice(8, 10)} ${(MON_SV[+k.slice(5, 7) - 1] ?? '').slice(0, 3)}`;

/** Today in Europe/Stockholm for an instant. */
export function stockholmDate(instant: Date): DateKey {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Hour (0–23) in Europe/Stockholm for an instant. */
export function stockholmHour(instant: Date): number {
  const h = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Stockholm', hour: '2-digit', hourCycle: 'h23' }).format(instant);
  return +h;
}

export const round1 = (n: number): number => Math.round(n * 10) / 10;
/** Rounds away floating noise (to 1/1000 h). */
export const roundH = (n: number): number => Math.round(n * 1000) / 1000;
